// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge, Alert, AppIcon, Button, IconButton, Modal, Spinner, SplitButton, TextInput } from '@loa/ui-components';
import { chartPalette, colors } from '@loa/design-tokens';
import {
  relationshipCashflowApi,
  type RelationshipCashflow,
  type Scenario,
} from '../api/relationshipCashflowApi';
import {
  financialPositionApi,
  type AccountSummary,
  type FigureOption,
} from '../api/financialPositionApi';
import { relationshipApi } from '../api/relationshipApi';
import { usePageHeader } from '../contexts/PageHeaderContext';
import { clientApi } from '../api/clientApi';
import { CashflowIncomeChart, type CashflowChartMode } from '../components/cashflow/CashflowIncomeChart';
import { CashflowChatRail } from '../components/cashflow/CashflowChatRail';
import { ScenarioParametersPanel } from '../components/cashflow/ScenarioParametersPanel';
import { CreateForecastModal } from '../components/cashflow/CreateForecastModal';
import { ScenarioCompareModal } from '../components/financialposition/ScenarioControls';

/**
 * The full-screen Cashflow Modelling workspace — two-panel design
 * (cashflow-modelling-tab PRD §5.2, editor redesign 2026-07):
 *
 * - LEFT "Scenarios" card: pill tabs with per-scenario dots (+ create), a
 *   stats strip (annual surplus / retires / inflation / forecast range), the
 *   stacked income chart with the "Retires" annotation, and Compare / view
 *   toggle / Export CSV / rename / delete actions.
 * - RIGHT editor panel with two tabs: "AI editor" (the CASHFLOW_COPILOT chat
 *   with applied-change cards) and "Parameters" (the grouped override list +
 *   add-override forms).
 *
 * Routes: /relationships/{profileId}/cashflow[/{scenarioId}].
 */

const money = (v: number | undefined | null): string =>
  v === undefined || v === null ? '—' : `€${Number(v).toLocaleString('en-IE')}`;

export function CashflowWorkspacePage() {
  const { profileId, scenarioId } = useParams<{ profileId: string; scenarioId?: string }>();
  const navigate = useNavigate();
  const { setBreadcrumbItems, setBackTo } = usePageHeader();

  const [profileName, setProfileName] = useState<string>('');
  const [primaryBirthYear, setPrimaryBirthYear] = useState<number | null>(null);
  const [clientNames, setClientNames] = useState<string[]>([]);
  const [cashflow, setCashflow] = useState<RelationshipCashflow | null>(null);
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [figures, setFigures] = useState<FigureOption[]>([]);
  const [sweepAccounts, setSweepAccounts] = useState<AccountSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rightTab, setRightTab] = useState<'ai' | 'params'>('ai');
  const [chartMode, setChartMode] = useState<CashflowChartMode>('combined');
  const [breakdown, setBreakdown] = useState<'grouped' | 'individual'>('grouped');
  const [creating, setCreating] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  const activeScenario = useMemo(
    () => scenarios.find((s) => s.id === scenarioId) ?? null,
    [scenarios, scenarioId],
  );

  const refresh = useCallback(() => {
    if (!profileId) return;
    Promise.all([
      relationshipCashflowApi.getCashflow(profileId, scenarioId ?? null),
      relationshipCashflowApi.listScenarios(profileId),
    ])
      .then(([cf, list]) => {
        setCashflow(cf);
        setScenarios(list);
        setError(cf === null ? 'Cashflow modelling is not enabled for your brokerage.' : null);
      })
      .catch(() => setError('Could not load the cashflow projection.'))
      .finally(() => setLoading(false));
  }, [profileId, scenarioId]);

  useEffect(refresh, [refresh]);

  // Breadcrumbs live in the app shell's page header (like the Max Funding
  // tool) — "Relationships / {name} / Cashflow Modelling" with back → the
  // relationship's Cashflow tab. Cleared on unmount.
  useEffect(() => {
    if (!profileId) return;
    const tab = `/relationships/${profileId}?tab=cashflow`;
    setBreadcrumbItems([
      { label: 'Relationships', to: '/relationships' },
      { label: profileName || 'Relationship', to: tab },
      { label: 'Cashflow Modelling' },
    ]);
    setBackTo(tab);
    return () => {
      setBreadcrumbItems(null);
      setBackTo(null);
    };
  }, [profileId, profileName, setBreadcrumbItems, setBackTo]);

  // Relationship context: breadcrumb name + primary client's birth year (age axis).
  useEffect(() => {
    if (!profileId) return;
    relationshipApi
      .getRelationship(profileId)
      .then((r) => setProfileName(r.profileName ?? r.relationshipName ?? ''))
      .catch(() => setProfileName(''));
    clientApi
      .getClientsByProfile(profileId)
      .then((clients) => {
        setClientNames(clients.map((c) => `${c.firstName} ${c.lastName}`.trim()));
        const primary = clients.find((c) => c.isPrimary) ?? clients[0];
        if (primary?.dateOfBirth) {
          const year = Number(primary.dateOfBirth.slice(0, 4));
          setPrimaryBirthYear(Number.isNaN(year) ? null : year);
        }
      })
      .catch(() => setClientNames([]));
  }, [profileId]);

  // Parameter-form option sources (figures + sweep-account labels).
  useEffect(() => {
    if (!profileId) return;
    financialPositionApi.getFigures(profileId).then(setFigures).catch(() => setFigures([]));
    financialPositionApi
      .listAccounts(profileId, 'ASSET')
      .then(setSweepAccounts)
      .catch(() => setSweepAccounts([]));
  }, [profileId]);

  if (!profileId) return null;

  const backToTab = () => navigate(`/relationships/${profileId}?tab=cashflow`);
  const openScenario = (id: string | null) =>
    navigate(id ? `/relationships/${profileId}/cashflow/${id}` : `/relationships/${profileId}/cashflow`);

  const exportCsv = () => {
    relationshipCashflowApi
      .downloadCashflowCsv(profileId, scenarioId ?? null)
      .catch(() => setError('Could not export the cashflow report.'));
  };

  const exportPdf = () => {
    relationshipCashflowApi
      .downloadCashflowPdf(profileId, scenarioId ?? null)
      .catch(() => setError('Could not export the PDF report.'));
  };

  const rename = () => {
    if (!scenarioId || busy || renameValue.trim() === '') return;
    setBusy(true);
    relationshipCashflowApi
      .patchScenario(profileId, scenarioId, { name: renameValue.trim() })
      .then(() => {
        setRenaming(false);
        refresh();
      })
      .catch(() => setError('Could not rename the forecast.'))
      .finally(() => setBusy(false));
  };

  const removeScenario = () => {
    if (!scenarioId || busy) return;
    setBusy(true);
    relationshipCashflowApi
      .deleteScenario(profileId, scenarioId)
      .then(() => backToTab())
      .catch(() => {
        setError('Could not delete the forecast.');
        setBusy(false);
      });
  };

  const taxAvailable = cashflow?.basis === 'GROSS' || cashflow?.taxUnavailable === true;

  // ── Stats strip (annual surplus / retires / inflation / forecast range). ──
  const year0 = cashflow?.years?.[0];
  const lastYear = cashflow?.years?.[cashflow.years.length - 1];
  const retirement = cashflow?.events?.find((e) => e.type === 'RETIREMENT');
  const retirementRow = retirement ? cashflow?.years?.[retirement.year] : undefined;
  const retiresLabel = retirementRow
    ? primaryBirthYear
      ? `Age ${retirementRow.calendarYear - primaryBirthYear}`
      : `${retirementRow.calendarYear}`
    : '—';
  const inflation = cashflow?.assumptions?.priceInflationRate;

  const overrideCount = activeScenario?.overrides.length ?? 0;

  const chipDot = (index: number): string => chartPalette[index % chartPalette.length];

  return (
    <div data-testid="cashflow-workspace" className="min-h-screen bg-page-bg flex flex-col">
      <div className="flex flex-1 min-h-0 gap-md p-md items-stretch">
        {/* ── LEFT: Scenarios card ── */}
        <section className="flex-1 min-w-0 bg-white rounded-card p-lg flex flex-col gap-md">
          <div className="flex items-center justify-between gap-sm flex-wrap">
            <h2 className="text-body-01 font-semibold text-black m-0">Scenarios</h2>
            <div className="flex items-center gap-sm">
              <Button variant="outlined" size="sm" data-testid="workspace-compare" onClick={() => setComparing(true)}>
                Compare
              </Button>
              <SplitButton<CashflowChartMode>
                size="sm"
                aria-label="Chart view"
                data-testid="workspace-view-toggle"
                value={chartMode}
                onChange={setChartMode}
                options={[
                  { value: 'cashflow', label: 'Cash Flow', 'data-testid': 'workspace-view-cashflow' },
                  { value: 'combined', label: 'Combined', 'data-testid': 'workspace-view-combined' },
                  { value: 'networth', label: 'Net Worth', 'data-testid': 'workspace-view-networth' },
                ]}
              />
              {/* Grouped/Individual applies to the cashflow bars AND the net-worth
                  line (Individual splits net worth into its components). */}
              <SplitButton<'grouped' | 'individual'>
                size="sm"
                aria-label="Breakdown"
                data-testid="workspace-breakdown-toggle"
                value={breakdown}
                onChange={setBreakdown}
                options={[
                  { value: 'grouped', label: 'Grouped', 'data-testid': 'workspace-breakdown-grouped' },
                  { value: 'individual', label: 'Individual', 'data-testid': 'workspace-breakdown-individual' },
                ]}
              />
              <Button variant="outlined" size="sm" data-testid="workspace-export" onClick={exportCsv}>
                Export CSV
              </Button>
              <Button variant="outlined" size="sm" data-testid="workspace-export-pdf" onClick={exportPdf}>
                Export PDF
              </Button>
              {scenarioId && (
                <>
                  <IconButton
                    variant="ghost"
                    size="sm"
                    data-testid="workspace-save"
                    aria-label="Rename forecast"
                    onClick={() => {
                      setRenameValue(activeScenario?.name ?? '');
                      setRenaming(true);
                    }}
                    icon={<AppIcon name="edit" size={16} color={colors.grey04} />}
                  />
                  <IconButton
                    variant="ghost"
                    size="sm"
                    data-testid="workspace-delete"
                    aria-label="Delete forecast"
                    onClick={() => setConfirmingDelete(true)}
                    icon={<AppIcon name="trash" size={16} color={colors.grey03} />}
                  />
                </>
              )}
            </div>
          </div>

          {/* Scenario pill tabs */}
          <div data-testid="workspace-chips" className="flex items-center gap-sm flex-wrap">
            <button
              type="button"
              data-testid="chip-base"
              onClick={() => openScenario(null)}
              className={`flex items-center gap-xs rounded-lg px-sm py-xs text-body-03 cursor-pointer border ${
                !scenarioId
                  ? 'border-purple-primary bg-lilac-05 text-purple-primary font-medium'
                  : 'border-transparent bg-transparent text-grey-04'
              }`}
            >
              <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: colors.grey03 }} />
              Base
            </button>
            {scenarios.map((s, i) => (
              <button
                key={s.id}
                type="button"
                data-testid="chip-scenario"
                onClick={() => openScenario(s.id)}
                className={`flex items-center gap-xs rounded-lg px-sm py-xs text-body-03 cursor-pointer border ${
                  scenarioId === s.id
                    ? 'border-purple-primary bg-lilac-05 text-purple-primary font-medium'
                    : 'border-transparent bg-transparent text-grey-04'
                }`}
              >
                <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: chipDot(i) }} />
                {s.name}
              </button>
            ))}
            <IconButton
              variant="ghost"
              size="sm"
              data-testid="chip-add"
              aria-label="Create new forecast"
              onClick={() => setCreating(true)}
              icon={<AppIcon name="plus" size={14} color={colors.purplePrimary} />}
            />
          </div>

          {/* Stats strip */}
          {cashflow && (
            <div data-testid="workspace-stats" className="flex items-center gap-lg flex-wrap border-t border-grey-06 pt-md">
              <div>
                <p className="text-body-04 text-grey-03 m-0">Annual surplus</p>
                <p data-testid="stat-surplus" className="text-body-02 font-mono text-success-text m-0">
                  {money(year0?.surplus)}
                </p>
              </div>
              <div>
                <p className="text-body-04 text-grey-03 m-0">Retires</p>
                <p data-testid="stat-retires" className="text-body-02 font-mono text-black m-0">{retiresLabel}</p>
              </div>
              <div>
                <p className="text-body-04 text-grey-03 m-0">Inflation</p>
                <p data-testid="stat-inflation" className="text-body-02 font-mono text-black m-0">
                  {inflation === null || inflation === undefined ? '—' : `${inflation}%`}
                </p>
              </div>
              <div>
                <p className="text-body-04 text-grey-03 m-0">Forecast</p>
                <p data-testid="stat-forecast" className="text-body-02 font-mono text-black m-0">
                  {year0 && lastYear ? `${year0.calendarYear}–${lastYear.calendarYear}` : '—'}
                </p>
              </div>
              {cashflow.basis === 'GROSS' && (
                <div>
                  <p className="text-body-04 text-grey-03 m-0">Tax (first year)</p>
                  <p data-testid="stat-tax" className="text-body-02 font-mono text-black m-0">
                    {money(year0?.tax)}
                    {year0 && year0.totalIncome > 0 && (
                      <span className="text-body-04 text-grey-03">
                        {' '}
                        ({Math.round((year0.tax / year0.totalIncome) * 100)}%)
                      </span>
                    )}
                  </p>
                </div>
              )}
              <Badge
                data-testid="workspace-basis-badge"
                color={cashflow.basis === 'GROSS' ? 'teal' : 'grey'}
                size="sm"
              >
                {cashflow.basis === 'GROSS' ? 'After tax (gross basis)' : 'Pre-tax (net basis)'}
              </Badge>
            </div>
          )}
          {cashflow?.taxUnavailable && (
            <Alert data-testid="workspace-tax-unavailable" variant="warning">
              This forecast asked for the taxed (gross) basis, but the tax layer is disabled for
              this environment — figures are shown pre-tax.
            </Alert>
          )}

          {error && <Alert variant="error">{error}</Alert>}
          {loading && <Spinner />}
          {!loading && cashflow && (
            <CashflowIncomeChart
              cashflow={cashflow}
              mode={chartMode}
              breakdown={breakdown}
              overrides={activeScenario?.overrides ?? []}
              primaryBirthYear={primaryBirthYear}
              onAnnotationClick={scenarioId ? () => setRightTab('params') : undefined}
            />
          )}
          {/* E1: first-year tax breakdown — the same component labels/order the
              engine emits (Income tax / USC / PRSI / DIRT / …). */}
          {!loading && cashflow?.basis === 'GROSS' && year0 && year0.tax > 0 && (
            <div
              data-testid="workspace-tax-breakdown"
              className="flex items-center gap-lg flex-wrap border-t border-grey-06 pt-md"
            >
              <p className="text-body-04 text-grey-03 m-0">Tax breakdown (first year)</p>
              {Object.entries(year0.taxByComponent ?? {}).map(([name, value]) => (
                <div key={name}>
                  <p className="text-body-04 text-grey-03 m-0">{name}</p>
                  <p className="text-body-03 font-mono text-black m-0">{money(value)}</p>
                </div>
              ))}
              <div>
                <p className="text-body-04 text-grey-03 m-0">Net income</p>
                <p data-testid="stat-net-income" className="text-body-03 font-mono text-success-text m-0">
                  {money(year0.netIncome)}
                </p>
              </div>
            </div>
          )}
        </section>

        {/* ── RIGHT: tabbed editor panel ── */}
        <aside
          data-testid="workspace-editor-panel"
          className="w-96 shrink-0 bg-white rounded-card flex flex-col min-h-0"
        >
          <div className="flex items-center gap-sm px-lg pt-md border-b border-grey-06">
            <button
              type="button"
              data-testid="editor-tab-ai"
              onClick={() => setRightTab('ai')}
              className={`flex items-center gap-xs bg-transparent border-0 border-b-2 px-xs pb-sm text-body-03 cursor-pointer ${
                rightTab === 'ai'
                  ? 'border-purple-primary text-black font-medium'
                  : 'border-transparent text-grey-04'
              }`}
            >
              <span className="w-4 h-4 rounded inline-block" style={{ backgroundColor: colors.purplePrimary }} />
              AI editor
            </button>
            <button
              type="button"
              data-testid="editor-tab-params"
              onClick={() => setRightTab('params')}
              className={`flex items-center gap-xs bg-transparent border-0 border-b-2 px-xs pb-sm text-body-03 cursor-pointer ${
                rightTab === 'params'
                  ? 'border-purple-primary text-black font-medium'
                  : 'border-transparent text-grey-04'
              }`}
            >
              Parameters
              <span className="rounded-full bg-lilac-04 text-purple-primary text-body-04 px-xs">
                {overrideCount}
              </span>
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto p-lg flex flex-col">
            {rightTab === 'ai' && (
              <CashflowChatRail
                profileId={profileId}
                scenarioId={scenarioId ?? null}
                scenarioName={activeScenario?.name ?? null}
                cashflow={cashflow}
                onScenarioChanged={refresh}
              />
            )}
            {rightTab === 'params' &&
              (activeScenario ? (
                <ScenarioParametersPanel
                  profileId={profileId}
                  scenario={activeScenario}
                  figures={figures}
                  sweepAccounts={sweepAccounts}
                  onChanged={refresh}
                />
              ) : (
                <p data-testid="parameters-base-hint" className="text-body-03 text-grey-03 m-0">
                  The base projection has no what-if parameters — open or create a forecast to add
                  changes.
                </p>
              ))}
          </div>
        </aside>
      </div>

      {creating && (
        <CreateForecastModal
          profileId={profileId}
          clientNames={clientNames}
          taxAvailable={taxAvailable}
          onClose={() => setCreating(false)}
          onCreated={(s) => {
            setCreating(false);
            openScenario(s.id);
          }}
        />
      )}
      {comparing && (
        <ScenarioCompareModal
          profileId={profileId}
          scenarios={scenarios}
          onClose={() => setComparing(false)}
        />
      )}
      {renaming && (
        <Modal open onClose={() => setRenaming(false)} size="small">
          <Modal.Header icon="edit" title="Rename forecast" onClose={() => setRenaming(false)} />
          <Modal.Content>
            <div className="flex flex-col gap-sm">
              <TextInput
                data-testid="workspace-rename-input"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                fluid
              />
              <div className="flex justify-end gap-sm">
                <Button variant="outlined" size="sm" onClick={() => setRenaming(false)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  data-testid="workspace-rename-confirm"
                  onClick={rename}
                  disabled={busy || renameValue.trim() === ''}
                >
                  Save
                </Button>
              </div>
            </div>
          </Modal.Content>
        </Modal>
      )}
      {confirmingDelete && (
        <Modal open onClose={() => setConfirmingDelete(false)} size="small">
          <Modal.Header icon="trash" title="Delete forecast" onClose={() => setConfirmingDelete(false)} />
          <Modal.Content>
            <div className="flex flex-col gap-sm">
              <p className="text-body-03 text-grey-01 m-0">
                Delete “{activeScenario?.name}”? Its what-if changes are removed; the base projection
                is unaffected.
              </p>
              <div className="flex justify-end gap-sm">
                <Button variant="outlined" size="sm" onClick={() => setConfirmingDelete(false)}>
                  Cancel
                </Button>
                <button
                  type="button"
                  data-testid="workspace-delete-confirm"
                  onClick={removeScenario}
                  disabled={busy}
                  className="rounded-lg border-0 bg-error-text px-md py-sm text-body-03 text-white cursor-pointer disabled:opacity-50"
                >
                  Delete
                </button>
              </div>
            </div>
          </Modal.Content>
        </Modal>
      )}
    </div>
  );
}

export default CashflowWorkspacePage;
